// ===========================================
// Registration Fees List (#374)
// ===========================================
// Each paid registration fee of the family with its child and what became of it.
// Rendered only when the server sent fees (it sends [] without registration_fees.read).
// Never says "Unpaid": a missing fee is not provable from the data.

import { Box, Button, Chip, IconButton, Link, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as RouterLink } from 'react-router-dom';
import type { RegistrationFeeView } from '../../../api/familyPayments.api';
import { PAYMENT_METHOD_LABELS, formatPaymentDay } from '../../../utils/invoiceDisplay';
import { formatCurrencyFromDollars } from '../../../utils/currency';

interface RegistrationFeesListProps {
  fees: RegistrationFeeView[];
  schoolSlug: string | undefined;
  timeZone: string;
  canAssign: boolean;
  onAssign: (fee: RegistrationFeeView) => void;
  /** Edit / delete of a MANUAL fee (operations_invoices write / delete), moved here from the status line. */
  canEdit?: boolean;
  canDelete?: boolean;
  onEdit?: (fee: RegistrationFeeView) => void;
  onDelete?: (fee: RegistrationFeeView) => void;
}

const iconButtonSx = { p: { xs: 1.5, sm: 0.5 }, minWidth: { xs: 44 }, minHeight: { xs: 44 } };

function StateLine({ fee, schoolSlug }: { fee: RegistrationFeeView; schoolSlug: string | undefined }) {
  switch (fee.state) {
    case 'HELD':
      return (
        <Chip
          size="small"
          color={fee.childActive === false ? 'default' : 'info'}
          variant="outlined"
          label={
            fee.childActive === false
              ? `Held (${fee.student?.name ?? 'the child'} is not active: not credited)`
              : `Held: credited on ${fee.student?.name ?? "the child"}'s first invoice`
          }
          // Wrap on a phone instead of truncating (360px).
          sx={{ height: 'auto', maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
        />
      );
    case 'APPLIED': {
      const short =
        fee.credit && parseFloat(fee.credit.creditedAmount) < parseFloat(fee.credit.worth)
          ? ` (credited ${formatCurrencyFromDollars(fee.credit.creditedAmount)} of ${formatCurrencyFromDollars(fee.credit.worth)})`
          : '';
      return (
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Chip size="small" color="success" variant="outlined" label="Applied" />
          {fee.credit && (
            <Typography variant="body2">
              <Link component={RouterLink} to={`/${schoolSlug}/admin/invoices/${fee.credit.invoiceId}`}>
                {fee.credit.invoiceNumber}
              </Link>
              {short}
            </Typography>
          )}
        </Stack>
      );
    }
    case 'FORFEITED':
      return <Chip size="small" variant="outlined" label="Forfeited" />;
    case 'UNASSIGNED':
      return <Chip size="small" color="warning" variant="outlined" label="Paid, no child assigned" />;
    case 'NOT_CREDITED':
    default:
      return <Chip size="small" variant="outlined" label="Paid" />;
  }
}

export default function RegistrationFeesList({
  fees,
  schoolSlug,
  timeZone,
  canAssign,
  onAssign,
  canEdit = false,
  canDelete = false,
  onEdit,
  onDelete,
}: RegistrationFeesListProps) {
  return (
    <Stack spacing={1} aria-label="Registration fees">
      {fees.map((fee) => (
        <Box
          key={fee.id}
          data-testid={`registration-fee-${fee.id}`}
          sx={{
            display: 'flex',
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">
              <strong>Registration fee{fee.student ? `: ${fee.student.name}` : ''}</strong>
              {' · '}
              {formatCurrencyFromDollars(fee.amount)} · {PAYMENT_METHOD_LABELS[fee.method]} ·{' '}
              {formatPaymentDay(fee.paidAt, timeZone)}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <StateLine fee={fee} schoolSlug={schoolSlug} />
            </Box>
            {fee.studentMatch === 'ONLY_CHILD' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Check the child: matched as the family's only child (the names differ)
                {canAssign && (
                  <Button size="small" onClick={() => onAssign(fee)} sx={{ ml: 1, minHeight: { xs: 44 } }}>
                    Change
                  </Button>
                )}
              </Typography>
            )}
          </Box>
          {fee.isManual && ((canEdit && onEdit) || (canDelete && onDelete)) && (
            <Stack direction="row" spacing={0.5}>
              {canEdit && onEdit && (
                <IconButton size="small" aria-label="Edit registration payment" sx={iconButtonSx} onClick={() => onEdit(fee)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {canDelete && onDelete && (
                <IconButton size="small" aria-label="Delete registration payment" sx={iconButtonSx} onClick={() => onDelete(fee)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          )}
          {fee.state === 'UNASSIGNED' && canAssign && (
            <Button size="small" onClick={() => onAssign(fee)} sx={{ whiteSpace: 'nowrap', minHeight: { xs: 44 } }}>
              Assign to child
            </Button>
          )}
        </Box>
      ))}
    </Stack>
  );
}
